import type { Types } from '@cornerstonejs/core';
import {
  RenderingEngine,
  Enums,
  getRenderingEngine,
  volumeLoader,
} from '@cornerstonejs/core';
import {
  initDemo,
  createImageIdsAndCacheMetaData,
  setTitleAndDescription,
  addButtonToToolbar,
  setCtTransferFunctionForVolumeActor,
  setPetColorMapTransferFunctionForVolumeActor,
} from '../../../../utils/demo/helpers';
import * as cornerstoneTools from '@cornerstonejs/tools';

// This is for debugging purposes
console.warn(
  'Click on index.ts to open source code for this example --------->'
);

const { ViewportType } = Enums;
const { segmentation } = cornerstoneTools;

// ======== Constants ======= //
const renderingEngineId = 'myRenderingEngine';
const viewportId = 'CT_STACK';

// Define unique ids for the volumes
const volumeLoaderScheme = 'cornerstoneStreamingImageVolume'; // Loader id which defines which volume loader to use
const ctVolumeName = 'CT_VOLUME_ID'; // Id of the volume less loader prefix
const ctVolumeId = `${volumeLoaderScheme}:${ctVolumeName}`; // VolumeId with loader id + volume id

// Define a unique id for the volume
const ptVolumeName = 'PT_VOLUME_ID';
const ptVolumeId = `${volumeLoaderScheme}:${ptVolumeName}`;

// Segmentation ID
const segmentationId = 'MY_SEGMENTATION_ID';

// Parse URL flags for comparison: ?url=<wadoRsRoot>&useLegacyMetadataProvider=true
const urlParams = new URLSearchParams(
  typeof window !== 'undefined' ? window.location.search : ''
);
const wadoRsRootFromUrl = urlParams.get('url') ?? urlParams.get('wadoRsRoot');
const useLegacyMetadataProvider =
  urlParams.get('useLegacyMetadataProvider') === 'true';

const defaultWadoRsRoot = 'https://d33do7qe4w26qo.cloudfront.net/dicomweb';
const wadoRsRoot = wadoRsRootFromUrl ?? defaultWadoRsRoot;

// ======== Set up page ======== //
const descParts: string[] = [];
if (useLegacyMetadataProvider) descParts.push('Legacy metadata provider');
if (wadoRsRootFromUrl) descParts.push(`URL: ${wadoRsRoot}`);
setTitleAndDescription(
  'Stack Viewport First then Add Overlay Volume',
  descParts.length ? descParts.join(' | ') : ''
);

const content = document.getElementById('content');
const element = document.createElement('div');
element.id = 'cornerstone-element';
element.style.width = '512px';
element.style.height = '512px';

content.appendChild(element);
let viewport: Types.IViewport;
let renderingEngine: RenderingEngine;
let ctImageIds, ptImageIds;
addButtonToToolbar({
  title: 'Stack to Volume',
  onClick: async () => {
    const viewPresentation = viewport.getViewPresentation();
    const viewReference = viewport.getViewReference();

    await convertStackToFusionVolume();
    viewport = renderingEngine.getViewport(viewportId);

    // viewport.render();

    console.warn(
      'Setting view presentation and reference',
      viewPresentation,
      viewReference
    );
    viewport.setViewPresentation(viewPresentation);
    viewport.setViewReference(viewReference);
    viewport.render();
  },
});

addButtonToToolbar({
  title: 'Stack to Volume with Labelmap',
  onClick: async () => {
    const viewReference = viewport.getViewReference();

    await convertStackToVolumeWithLabelmap();
    viewport = renderingEngine.getViewport(viewportId);

    viewport.setViewReference(viewReference);
    viewport.render();
  },
});

/**
 * Runs the demo
 */
async function run() {
  // Init Cornerstone and related libraries (useLegacyMetadataProvider from URL for comparison)
  await initDemo({ useLegacyMetadataProvider });

  ptImageIds = await createImageIdsAndCacheMetaData({
    StudyInstanceUID:
      '1.3.6.1.4.1.14519.5.2.1.7009.2403.334240657131972136850343327463',
    SeriesInstanceUID:
      '1.3.6.1.4.1.14519.5.2.1.7009.2403.879445243400782656317561081015',
    wadoRsRoot,
  });

  ctImageIds = await createImageIdsAndCacheMetaData({
    StudyInstanceUID:
      '1.3.6.1.4.1.14519.5.2.1.7009.2403.334240657131972136850343327463',
    SeriesInstanceUID:
      '1.3.6.1.4.1.14519.5.2.1.7009.2403.226151125820845824875394858561',
    wadoRsRoot,
  });

  // Instantiate a rendering engine
  renderingEngine = new RenderingEngine(renderingEngineId);

  // Create a stack viewport
  const viewportInput = {
    viewportId,
    type: ViewportType.STACK,
    element,
    defaultOptions: {
      background: [0.2, 0, 0.2] as Types.Point3,
    },
  };

  renderingEngine.enableElement(viewportInput);

  // Get the stack viewport that was created
  viewport = renderingEngine.getViewport(viewportId);
  const stackViewport = viewport as Types.IStackViewport;

  // Set the stack on the viewport
  await stackViewport.setStack(ctImageIds);

  // Create and cache both volumes
  const ctVolume = await volumeLoader.createAndCacheVolume(ctVolumeId, {
    imageIds: ctImageIds,
  });

  const ptVolume = await volumeLoader.createAndCacheVolume(ptVolumeId, {
    imageIds: ptImageIds,
  });

  // Load the volumes
  await ctVolume.load();
  await ptVolume.load();

  // Create a segmentation of the same resolution as the source data
  volumeLoader.createAndCacheDerivedLabelmapVolume(ctVolumeId, {
    volumeId: segmentationId,
  });

  // Render the image - note the offset/zoom is specifically set to to be re-rendered after the viewport is updated
  viewport.render();
  viewport.setZoom(2.4);
  viewport.setPan([-100, -100]);
}

/**
 * Converts stack viewport to volume viewport with fusion
 */
function convertStackToFusionVolume() {
  // Disable the current viewport
  const renderingEngine = getRenderingEngine(renderingEngineId);
  renderingEngine.disableElement(viewportId);

  // Create new volume viewport
  const viewportInput = {
    viewportId,
    type: ViewportType.ORTHOGRAPHIC,
    element,
    defaultOptions: {
      orientation: Enums.OrientationAxis.AXIAL,
      background: [0.5, 0.7, 0.5] as Types.Point3,
    },
  };

  // Enable the volume viewport
  renderingEngine.enableElement(viewportInput);

  viewport = renderingEngine.getViewport(viewportId);
  const volumeViewport = viewport as Types.IVolumeViewport;

  // Set both volumes on the viewport with fusion
  return volumeViewport.setVolumes([
    {
      volumeId: ctVolumeId,
      callback: setCtTransferFunctionForVolumeActor,
    },
    {
      volumeId: ptVolumeId,
      callback: setPetColorMapTransferFunctionForVolumeActor,
    },
  ]);
}

/**
 * Converts stack viewport to volume viewport with a labelmap for segmentation
 */
async function convertStackToVolumeWithLabelmap() {
  // Disable the current viewport
  const renderingEngine = getRenderingEngine(renderingEngineId);
  renderingEngine.disableElement(viewportId);

  // Create new volume viewport
  const viewportInput = {
    viewportId,
    type: ViewportType.ORTHOGRAPHIC,
    element,
    defaultOptions: {
      orientation: Enums.OrientationAxis.AXIAL,
      background: [0.2, 0.7, 0.2] as Types.Point3,
    },
  };

  // Enable the volume viewport
  renderingEngine.enableElement(viewportInput);

  viewport = renderingEngine.getViewport(viewportId) as Types.IVolumeViewport;

  // Add the segmentation to state
  await segmentation.addSegmentations([
    {
      segmentationId,
      representation: {
        type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
        data: {
          volumeId: segmentationId,
        },
      },
    },
  ]);

  const volumeViewport = viewport as Types.IVolumeViewport;
  // Set the volume on the viewport
  await volumeViewport.setVolumes([
    {
      volumeId: ctVolumeId,
      callback: setCtTransferFunctionForVolumeActor,
    },
  ]);

  // Add the segmentation representation to the viewport
  const segmentationRepresentation = {
    segmentationId,
    type: cornerstoneTools.Enums.SegmentationRepresentations.Labelmap,
  };

  await segmentation.addLabelmapRepresentationToViewport(viewportId, [
    segmentationRepresentation,
  ]);
}

run();
